import { useEffect, useRef, useState, RefObject } from "react";
import { jwtDecode } from "jwt-decode";
import useGetAllUsers from "../../../hooks/useGetAllUsers";
import { usePostForm } from "../../../context/PostFormContext";
import RecipientAvatar from "../../recipientButton/RecipientAvatar";
import { CreateUserProps, TokenPayload } from "../../../types/ComponentProps";
import RecipientButtonStyles from "./RecipientButtonStyles.module.css";
import Spinner from "../../spinner/Spinner";
import { MentionInputHandle } from "../mentionInput/MentionInput";

type RecipientButtonProps = {
  isOpen: boolean;
  toggleDropdown: () => void;
  mentionInputRef: RefObject<MentionInputHandle>;
};

const RecipientButton = ({
  isOpen,
  toggleDropdown,
  mentionInputRef,
}: RecipientButtonProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { formPost, setFormPost } = usePostForm();
  const { getAllUsersData, isLoading } = useGetAllUsers();
  const users: CreateUserProps[] = getAllUsersData?.users || [];

  const userDetails = jwtDecode<TokenPayload>(
    JSON.stringify(localStorage.getItem("accessTokenGC")),
  );

  const availableRecipients = users
    .filter(
      (user) =>
        user.id !== userDetails.id &&
        !formPost.recipients?.some((recipient) => recipient.id === user.id),
    )
    .filter((user) => {
      const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
      const email = user.email.toLowerCase();
      const search = searchTerm.toLowerCase();
      return fullName.includes(search) || email.includes(search);
    });

  const onRecipientSelect = (user: CreateUserProps) => {
    mentionInputRef.current?.appendMentionChip(user);
    toggleDropdown();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        toggleDropdown(); // close dropdown on outside click
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, toggleDropdown]);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
    }
  }, [isOpen]);

  return (
    <div className={RecipientButtonStyles.containerBlk} ref={dropdownRef}>
      <button
        className={RecipientButtonStyles.buttonBlk}
        onClick={toggleDropdown}
      >
        <i className="fa-solid fa-at"></i> Recipient
      </button>

      {isOpen && (
        <div className={RecipientButtonStyles.recipientDropDownListBlk}>
          {isLoading ? (
            <Spinner variant="medium-spinner" alignSpin="center-spinner" />
          ) : (
            <>
              <div className={RecipientButtonStyles.searchBlk}>
                <input
                  type="text"
                  placeholder="Search User..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <div className={RecipientButtonStyles.recipientScrollBlk}>
                {availableRecipients.length === 0 ? (
                  <p className={RecipientButtonStyles.emptyText}>
                    No users found.
                  </p>
                ) : (
                  availableRecipients.map((user) => (
                    <div
                      key={user.id}
                      className={RecipientButtonStyles.recipientItem}
                      onClick={() => onRecipientSelect(user)}
                      role="button"
                      tabIndex={0}
                    >
                      <div className={RecipientButtonStyles.flexBoxBlk}>
                        <div>
                          <RecipientAvatar userId={Number(user?.id)} />
                        </div>
                        <div>
                          <p className={RecipientButtonStyles.userName}>
                            {user.firstName} {user.lastName}
                          </p>
                          <p className={RecipientButtonStyles.userEmail}>
                            {user.email}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default RecipientButton;
